"use client";

import {
  FIRST_RUN_INTRO_EXPERIMENT_KEY,
  FIRST_RUN_INTRO_VARIANTS,
  type FirstRunIntroVariant,
  parseFirstRunIntroVariant,
} from "@/lib/experiments/firstRunIntro";
import { useEffect, useState } from "react";

type FeatureFlagLoadContext = { errorsLoading?: boolean };

export type FirstRunIntroPostHogClient = {
  getFeatureFlagResult: (
    key: string,
    options?: { fresh?: boolean; send_event?: boolean }
  ) => { variant?: string } | undefined;
  onFeatureFlags: (
    callback: (
      flags: string[],
      variants: Record<string, string | boolean>,
      context?: FeatureFlagLoadContext
    ) => void
  ) => () => void;
};

export type FirstRunIntroResolution = {
  status: "loading" | "resolved";
  variant: FirstRunIntroVariant;
  source: "pending" | "posthog" | "fallback" | "bypassed";
  reason?: "disabled" | "invalid_variant" | "load_error" | "timeout" | "unavailable";
};

const LOADING: FirstRunIntroResolution = {
  status: "loading",
  variant: FIRST_RUN_INTRO_VARIANTS.control,
  source: "pending",
};

function fallback(
  reason: NonNullable<FirstRunIntroResolution["reason"]>,
  source: "fallback" | "bypassed" = "fallback"
): FirstRunIntroResolution {
  return { status: "resolved", variant: FIRST_RUN_INTRO_VARIANTS.control, source, reason };
}

const BYPASSED = fallback("disabled", "bypassed");

/**
 * Resolves the intro's variant from the first flags load. The first screen
 * waits for it, so unlike the other guest experiments this does not request a
 * fresh reload, and it falls back to control quickly.
 */
export function useFirstRunIntroExperiment({
  enabled,
  posthog,
  timeoutMs = 1_000,
}: {
  enabled: boolean;
  posthog: FirstRunIntroPostHogClient | null | undefined;
  timeoutMs?: number;
}): FirstRunIntroResolution {
  const [resolution, setResolution] = useState<FirstRunIntroResolution>(() =>
    enabled ? LOADING : BYPASSED
  );

  useEffect(() => {
    if (!enabled) {
      setResolution(BYPASSED);
      return;
    }
    if (!posthog) {
      setResolution(fallback("unavailable"));
      return;
    }

    let settled = false;
    let unsubscribe: () => void = () => undefined;
    setResolution(LOADING);

    const finish = (next: FirstRunIntroResolution) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      setResolution(next);
    };
    const timeoutId = window.setTimeout(() => finish(fallback("timeout")), timeoutMs);

    try {
      // Called at once when the flags have already loaded.
      unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
        if (settled) return;
        if (context?.errorsLoading) {
          finish(fallback("load_error"));
          return;
        }
        try {
          const result = posthog.getFeatureFlagResult(FIRST_RUN_INTRO_EXPERIMENT_KEY, {
            send_event: false,
          });
          const variant = parseFirstRunIntroVariant(result?.variant);
          finish(
            variant
              ? { status: "resolved", variant, source: "posthog" }
              : fallback("invalid_variant")
          );
        } catch {
          finish(fallback("unavailable"));
        }
      });
    } catch {
      finish(fallback("unavailable"));
    }

    return () => {
      settled = true;
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [enabled, posthog, timeoutMs]);

  // The state only catches up with `enabled` in the effect. In the render where
  // a guest becomes eligible it still says "bypassed", which would let the page
  // mount as control; cached flags then resolve in that same effect, so no
  // loading render ever follows. Report the change from the render itself.
  if (enabled && resolution.source === "bypassed") return LOADING;
  if (!enabled && resolution.source !== "bypassed") return BYPASSED;
  return resolution;
}

/** Records the exposure once the variant's first screen is on display. */
export function exposeFirstRunIntroExperiment(
  posthog: FirstRunIntroPostHogClient,
  renderedVariant: FirstRunIntroVariant
): boolean {
  try {
    const current = posthog.getFeatureFlagResult(FIRST_RUN_INTRO_EXPERIMENT_KEY, {
      send_event: false,
    });
    if (parseFirstRunIntroVariant(current?.variant) !== renderedVariant) return false;
    posthog.getFeatureFlagResult(FIRST_RUN_INTRO_EXPERIMENT_KEY, { send_event: true });
    return true;
  } catch {
    return false;
  }
}
